import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { allIPhoneModels } from "@/data/allIPhoneModels";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Search, Check, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface CustomImage {
  id: string;
  model_id: string;
  image_url: string;
}

interface IPhoneImageManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const IPhoneImageManager = ({ isOpen, onClose }: IPhoneImageManagerProps) => {
  const [customImages, setCustomImages] = useState<CustomImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) fetchCustomImages();
  }, [isOpen]);

  const fetchCustomImages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("iphone_custom_images")
      .select("*");
    if (!error && data) setCustomImages(data);
    setLoading(false);
  };

  const handleUploadClick = (modelId: string) => {
    setSelectedModelId(modelId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedModelId) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }

    setUploading(selectedModelId);
    try {
      const ext = file.name.split(".").pop();
      const path = `iphone-comparison/${selectedModelId}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("product-images")
        .upload(path, file, { upsert: true });
      if (uploadErr) throw uploadErr;

      const { data: { publicUrl } } = supabase.storage
        .from("product-images")
        .getPublicUrl(path);

      // Add cache-bust param
      const url = `${publicUrl}?t=${Date.now()}`;

      const existing = customImages.find(ci => ci.model_id === selectedModelId);
      if (existing) {
        const { error } = await supabase
          .from("iphone_custom_images")
          .update({ image_url: url })
          .eq("model_id", selectedModelId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("iphone_custom_images")
          .insert({ model_id: selectedModelId, image_url: url });
        if (error) throw error;
      }

      await fetchCustomImages();
      toast.success("Immagine aggiornata!");
    } catch (err: any) {
      console.error(err);
      toast.error("Errore upload: " + (err.message || ""));
    } finally {
      setUploading(null);
      setSelectedModelId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (modelId: string) => {
    try {
      const { error } = await supabase
        .from("iphone_custom_images")
        .delete()
        .eq("model_id", modelId);
      if (error) throw error;
      setCustomImages(prev => prev.filter(ci => ci.model_id !== modelId));
      toast.success("Immagine rimossa");
    } catch (err: any) {
      toast.error("Errore rimozione");
    }
  };

  const filtered = allIPhoneModels.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCustomImageUrl = (modelId: string) =>
    customImages.find(ci => ci.model_id === modelId)?.image_url;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} shouldScaleBackground={false}>
      <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
        <DrawerHeader className="relative border-b border-border/30 pb-4">
          <button
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <DrawerTitle className="text-xl font-bold text-center">Immagini iPhone</DrawerTitle>
        </DrawerHeader>

        <div className="p-4">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca modello..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-muted/50"
            />
          </div>
        </div>

        <ScrollArea className="flex-1 px-4 pb-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filtered.map(model => {
                const customUrl = getCustomImageUrl(model.id);
                const isUploading = uploading === model.id;

                return (
                  <div
                    key={model.id}
                    className="rounded-xl border border-border/50 bg-muted/30 overflow-hidden"
                  >
                    {/* Image preview */}
                    <div className="aspect-square bg-muted/50 relative flex items-center justify-center">
                      {customUrl ? (
                        <>
                          <img
                            src={customUrl}
                            alt={model.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-1 right-1 bg-primary/90 rounded-full p-1">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                        </>
                      ) : (
                        <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
                      )}
                      {isUploading && (
                        <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                        </div>
                      )}
                    </div>

                    {/* Info + Actions */}
                    <div className="p-2">
                      <p className="text-xs font-medium text-foreground truncate">{model.name}</p>
                      <p className="text-[10px] text-muted-foreground">{model.year}</p>
                      <div className="flex gap-1.5 mt-2">
                        <button
                          onClick={() => handleUploadClick(model.id)}
                          disabled={isUploading}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                        >
                          <Upload className="w-3 h-3" />
                          {customUrl ? "Cambia" : "Carica"}
                        </button>
                        {customUrl && (
                          <button
                            onClick={() => handleRemove(model.id)}
                            className="flex items-center justify-center p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </DrawerContent>
    </Drawer>
  );
};

export default IPhoneImageManager;
